import type { CSSProperties } from "react";
import WheelFire from "@/components/WheelFire";

// Colour sets for the aura. Everything downstream reads the --fire-* variables,
// so adding a palette or recolouring the fire is a one-line change here (and the
// `palette` prop lets a caller pick one, e.g. a blue aura for a future mode).
export const FIRE_PALETTES = {
  fire: {
    core: "#fff1a8",
    mid: "#ff8a1e",
    base: "#ff2400",
    glow: "rgba(255, 95, 0, 0.55)",
  },
  blue: {
    core: "#d6f0ff",
    mid: "#39a8ff",
    base: "#0b4dff",
    glow: "rgba(40, 130, 255, 0.55)",
  },
  void: {
    core: "#f1c7ff",
    mid: "#a94dff",
    base: "#5a12d6",
    glow: "rgba(150, 50, 255, 0.55)",
  },
  toxic: {
    core: "#ecffb3",
    mid: "#86ff2e",
    base: "#16a000",
    glow: "rgba(95, 220, 0, 0.55)",
  },
} as const;

export type FirePalette = keyof typeof FIRE_PALETTES;

// To change the default fire colour everywhere, switch this one constant.
const DEFAULT_PALETTE: FirePalette = "fire";

export default function WheelHeat({
  heat,
  palette = DEFAULT_PALETTE,
}: {
  heat: number;
  palette?: FirePalette;
}) {
  if (heat <= 0) return null;
  const lvl = Math.min(3, Math.max(1, Math.round(heat))) as 1 | 2 | 3;
  const p = FIRE_PALETTES[palette];

  const rootStyle = {
    "--heat": String(lvl),
    "--fire-core": p.core,
    "--fire-mid": p.mid,
    "--fire-base": p.base,
    "--fire-glow": p.glow,
  } as CSSProperties;

  return (
    <div className="wheel-aura" style={rootStyle} aria-hidden>
      {/* Soft CSS halo for ambient bloom behind the fire. */}
      <div className="aura-glow" />

      {/* The fire: an SVG flame corona + 2D-canvas embers (no WebGL). */}
      <WheelFire
        intensity={0.55 + lvl * 0.15}
        colors={{ core: p.core, mid: p.mid, base: p.base }}
      />
    </div>
  );
}
